<script>
import { GlLink } from '@gitlab/ui';
import SafeHtml from '~/vue_shared/directives/safe_html';
import CodeBlock from '~/vue_shared/components/code_block.vue';
import {
  unselectedInlineNumberMark,
  numMarkerIdPrefix,
} from 'ee/vue_shared/components/code_flow/utils/constants';
import {
  markdownBlobData,
  scrollToSpecificCodeFlow,
} from 'ee/vue_shared/components/code_flow/utils/utils';

export default {
  name: 'VulnerabilityFileContentViewer',
  components: { CodeBlock, GlLink },
  directives: {
    SafeHtml,
  },
  props: {
    startLine: { type: Number, required: false, default: null },
    endLine: { type: Number, required: false, default: null },
    isHighlighted: { type: Boolean, required: false, default: false },
    content: { type: String, required: true },
    highlightInfo: { type: Array, required: false, default: () => [] },
    selectedStepNumber: { type: Number, required: false, default: undefined },
  },
  computed: {
    lineNumbers() {
      const linesNumbers = this.highlightInfo.map((highlight) => {
        return {
          lineNumber: highlight.startLine,
          stepNumber: highlight.stepNumber,
        };
      });
      return Array.from({ length: this.endLine - this.startLine + 1 }, (_, i) => {
        const currentLineNumber = this.startLine + i;
        return {
          lineNumber: currentLineNumber,
          // Combines the step numbers into a comma-separated string
          stepNumbers: linesNumbers
            .filter((line) => line.lineNumber === currentLineNumber)
            .map((step) => step.stepNumber),
        };
      });
    },
    sourceCodeLines() {
      const lines = this.content.split('\n');
      // content is the entire file, the GraphQL query does not have the ability to get only certain lines of the
      // file. Because we only need certain lines, we'll split the lines into an array, slice out only those lines, then
      // rejoin it back into a string so that the code block component can render it properly.
      return lines
        .slice(Math.max(this.startLine - 1, 0), Math.min(this.endLine, lines.length))
        .map((line) => line || ' ') // to display empty lines as well!
        .join('\n');
    },
    userColorScheme() {
      return gon.user_color_scheme;
    },
  },
  watch: {
    /**
     * when the user selects a different step in the code flow
     * we need to highlight the new row and scroll to it
     */
    selectedStepNumber() {
      markdownBlobData(this.selectedStepNumber);
      scrollToSpecificCodeFlow(this.selectedStepNumber);
    },
  },
  mounted() {
    /**
     * when the user closes and opens the collapse, this component is mounted again,
     * so we need to highlight the new row and scroll to it
     */
    markdownBlobData(this.selectedStepNumber);
    scrollToSpecificCodeFlow(this.selectedStepNumber);
  },
  /**
   * 1. when the component is load for the first time, the sourceCodeLines is empty
   * (because of async function 'highlightContent' in the parent),
   * so when sourceCodeLines is updated, we need to highlight the new row and scroll to it
   * 2. when the user opens more code blocks, this component is updated,
   * so we need to highlight the new row and scroll to it
   */
  updated() {
    markdownBlobData(this.selectedStepNumber);
    scrollToSpecificCodeFlow(this.selectedStepNumber);
  },
  methods: {
    lineNumberId(line) {
      return `${numMarkerIdPrefix}${line.stepNumbers}-L${line.lineNumber}`;
    },
  },
  unselectedInlineNumberMarkClassName: unselectedInlineNumberMark,
};
</script>

<template>
  <div class="file-holder">
    <div
      :class="userColorScheme"
      class="code blob-viewer file-content gl-flex gl-items-center gl-overflow-auto gl-rounded-base"
    >
      <div
        class="line-numbers diff-line-num gl-border-r gl-sticky gl-left-0 gl-z-1 !gl-min-w-fit !gl-pl-3 !gl-pr-0"
      >
        <gl-link
          v-for="line in lineNumbers"
          :id="lineNumberId(line)"
          :key="line.lineNumber"
          class="file-line-num gl-cursor-default !gl-no-underline"
          :class="$options.unselectedInlineNumberMarkClassName"
          data-testid="line-number"
        >
          {{ line.lineNumber }}
        </gl-link>
      </div>

      <code-block
        class="highlight gl-grow !gl-overflow-visible !gl-border-none !gl-p-0 !gl-leading-20"
      >
        <code
          v-if="isHighlighted"
          v-safe-html="sourceCodeLines"
          class="!gl-block"
          :class="{ '!gl-whitespace-pre': highlightInfo.length === 0 }"
        ></code>
        <span v-else class="plain-lines">{{ sourceCodeLines }}</span>
      </code-block>
    </div>
  </div>
</template>
